import { XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { IndustryEnum } from '@dashboard/enums/industry';

import { useAdminMerchantsUsage } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { cn } from '@dashboard/lib/utils';
import { formatPrice } from '@dashboard/services/formatting';
import plans from '@dashboard/services/plans';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import { getRenewalStatus } from './merchantUtils';

const planHandles = plans.reduce((acc, plan) => {
  if (!acc.find(p => p.handle === plan.handle)) {
    acc.push(plan);
  }

  return acc;
}, []);

const MerchantsUsage = () => {
  const history = useHistory();

  const urlQuery = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput(urlQuery.search);
  const [sortBy, setSortBy] = useState(urlQuery.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(Number(urlQuery.sortDirection) || -1);
  const [limit] = useState(20);
  const [plan, setPlan] = useState(urlQuery.plan || '');
  const [billingPeriod, setBillingPeriod] = useState(urlQuery.billingPeriod || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    urlQuery.subscriptionStatus || 'active',
  );
  const [industry, setIndustry] = useState(urlQuery.industry || '');
  const [hasCustomPricing, setHasCustomPricing] = useState(urlQuery.hasCustomPricing || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(plan && { plan }),
        ...(billingPeriod && { billingPeriod }),
        ...(subscriptionStatus && { subscriptionStatus }),
        ...(industry && { industry }),
        ...(hasCustomPricing && { hasCustomPricing }),
        ...(search && { search }),
      }),
    });
  }, [
    sortBy,
    sortDirection,
    plan,
    subscriptionStatus,
    industry,
    hasCustomPricing,
    search,
    billingPeriod,
  ]);

  const filters = {
    sortBy,
    sortDirection,
    plan,
    billingPeriod,
    subscriptionStatus,
    industry,
    hasCustomPricing,
    search,
    limit,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isPlaceholderData } =
    useAdminMerchantsUsage(filters, {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 60 * 24,
    });

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      // A new column opens highest first: the revenue columns are almost all $0 at the low end.
      const nextDesc = id === sortBy ? desc : true;
      setSortBy(id);
      setSortDirection(nextDesc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns - 16 columns for MerchantsUsage
  const columns = useMemo(
    () => [
      {
        accessorKey: 'displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Store" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return (
            <div className="max-w-xs">
              <Link to={`/admin/merchants/${merchant._id}`}>
                {merchant.displayName || merchant.name || 'N/A'}
              </Link>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'industry',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Store focus" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return merchant.industry ? upperFirst(merchant.industry.replace(/-/g, ' ')) : 'N/A';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'planHandle',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Plan" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return (
            <div>
              {merchant?.plan?.title}
              {merchant?.plan?.handle === 'scale' && merchant?.plan?.bracket && (
                <span> {merchant.plan.bracket / 1000}K</span>
              )}
              {merchant?.plan?.billingPeriod && <span> - {merchant?.plan?.billingPeriod}</span>}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'monthlyRecurringRevenue',
        header: ({ column }) => <DataGridColumnHeader column={column} title="MRR" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return (
            <div>
              {formatPrice({
                amount: merchant.monthlyRecurringRevenue,
                currency: 'usd',
                style: 'currency',
              })}{' '}
              / month
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'totalRevenueThisMonth',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Revenue this month" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return formatPrice({
            amount: merchant.totalRevenueThisMonth,
            currency: 'usd',
            style: 'currency',
          });
        },
        enableSorting: true,
      },
      {
        accessorKey: 'totalRevenueLastMonth',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Revenue last month" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return (
            <span className="text-gray-500 dark:text-muted-foreground">
              {formatPrice({
                amount: merchant.totalRevenueLastMonth,
                currency: 'usd',
                style: 'currency',
              })}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'totalRevenueLast12Months',
        header: ({ column }) => (
          <DataGridColumnHeader column={column} title="Revenue last 12 months" />
        ),
        cell: ({ row }) => {
          const merchant = row.original;
          return formatPrice({
            amount: merchant.totalRevenueLast12Months,
            currency: 'usd',
            style: 'currency',
          });
        },
        enableSorting: true,
      },
      {
        accessorKey: 'pages',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Pages" />,
        cell: ({ row }) => row.original.pages,
        enableSorting: true,
      },
      {
        accessorKey: 'teamMembersSessions',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            <DataGridColumnHeader column={column} title="Sessions" />
            <InfoTooltip>Total sessions for all team members combined</InfoTooltip>
          </div>
        ),
        cell: ({ row }) => row.original.teamMembersSessions,
        enableSorting: true,
      },
      {
        accessorKey: 'hasCustomDomain',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Custom domain" />,
        cell: ({ row }) => (row.original.hasCustomDomain ? 'Yes' : 'No'),
        enableSorting: true,
      },
      {
        accessorKey: 'teamMembersCount',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Team members" />,
        cell: ({ row }) => row.original.teamMembersCount,
        enableSorting: true,
      },
      {
        accessorKey: 'webhooksCount',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Webhooks" />,
        cell: ({ row }) => row.original.webhooksCount,
        enableSorting: true,
      },
      {
        accessorKey: 'checkoutAbandonment.collectEmails',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Collect emails?" />,
        cell: ({ row }) => (row.original.checkoutAbandonment?.collectEmails ? 'Yes' : 'No'),
        enableSorting: true,
      },
      {
        accessorKey: 'checkoutAbandonment.sendEmails',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Send emails?" />,
        cell: ({ row }) => (row.original.checkoutAbandonment?.sendEmails ? 'Yes' : 'No'),
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Signed up at" />,
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'd MMM, yyyy, HH:mm') : '';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'currentPeriodEnds',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const merchant = row.original;
          return <div>{getRenewalStatus(merchant, true)}</div>;
        },
        enableSorting: true,
      },
    ],
    [],
  );

  const total = data?.pages[0]?.total;

  // Filter controls
  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto text-sm text-gray-500 dark:text-muted-foreground whitespace-nowrap">
        {typeof total === 'number' &&
          `${total.toLocaleString()} ${total === 1 ? 'merchant' : 'merchants'}`}
      </div>
      <FormElement className="!mb-0">
        <Label htmlFor="industry">Store focus</Label>
        <Select
          name="industry"
          value={industry || 'all'}
          onValueChange={value => setIndustry(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {Object.values(IndustryEnum).map(currentAccountType => (
              <SelectItem key={currentAccountType} value={currentAccountType}>
                {upperFirst(currentAccountType)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="plan">Plan</Label>
        <Select
          name="plan"
          value={plan || 'all'}
          onValueChange={value => {
            setPlan(value === 'all' ? '' : value);

            if (!subscriptionStatus) {
              setSubscriptionStatus('active');
            }
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {planHandles.map(planOption => (
              <SelectItem key={planOption.handle} value={planOption.handle}>
                {planOption.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="billingPeriod">Billing period</Label>
        <Select
          name="billingPeriod"
          value={billingPeriod || 'all'}
          onValueChange={value => {
            setBillingPeriod(value === 'all' ? '' : value);

            if (!subscriptionStatus) {
              setSubscriptionStatus('active');
            }
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="year">Year</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="subscriptionStatus">Subscription status</Label>
        <Select
          name="subscriptionStatus"
          value={subscriptionStatus || 'all'}
          onValueChange={value => setSubscriptionStatus(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="trial_expired">Trial expired</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="legacy">Legacy</SelectItem>
            <SelectItem value="comped">Comped</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="hasCustomPricing">Custom pricing</Label>
        <Select
          name="hasCustomPricing"
          value={hasCustomPricing || 'all'}
          onValueChange={value => setHasCustomPricing(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name, email, ID"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </FormElement>
    </div>
  );

  return (
    <React.Fragment>
      <div className="px-4 mb-4">{filterControls}</div>

      <div
        aria-busy={isPlaceholderData || undefined}
        className={cn('transition-opacity', isPlaceholderData && 'opacity-50')}
      >
        <DataGrid
          columns={columns}
          data={flatData}
          isLoading={isLoading}
          paginationMode="infinite"
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          emptyState={
            <EmptyState>
              <EmptyState.Title>No merchants found</EmptyState.Title>
            </EmptyState>
          }
        />
      </div>
    </React.Fragment>
  );
};

export default MerchantsUsage;
